import moment from "moment";

import {
    get_counterpart,
    get_counterpart_month_docs,
    get_counterpart_monthly_history,
    resolve_counterpart_by_alias,
} from "@/api/carteraAnalista";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";

// Drill-down de segundo nivel: el modal de análisis de Ventas abre este
// drawer cuando el usuario hace click sobre un cliente del ranking. Módulo
// independiente para que futuros tabs (Gastos: proveedor del mes; Costos:
// proveedor del mes) clonen el patrón sin disentangle de salesAnalysis.

// Las opciones del selector reflejan los 3 últimos items del período del
// CLASIFICADOR (`store/classification.js → initClassificationFilters`):
// `last12` / `last24` / `todo`, con los mismos `value` y label. Compartir el
// `value` es lo que permite que, al abrir un documento y saltar al clasificador
// (DocumentsDetailCard.goToClassifier), éste pueda reabrirse en el MISMO rango
// (DashboardClassificator.consumeClassifyDeepLink).
//
// El selector vive en el header del drawer y mueve toda la lectura del rango:
// chart de Historial, tabla de Documentos y KPIs derivados (Total del rango,
// Documentos del rango). El rango es independiente del mes clickeado en la
// tabla (`monthIso`): la fecha fin es siempre HOY. `monthIso` sólo ancla la
// lectura de un mes puntual (computeMonthBoundaries).
//
// `monthsBack` se sustrae al mes EN CURSO. `null` = sin tope inferior por meses
// ("Toda la historia"): cae al mismo piso histórico que el clasificador.
const HISTORY_FLOOR = "2015-01-01";

export const RANGE_OPTIONS = [
    { value: "last12", label: "Últimos 12 meses", monthsBack: 11 },
    { value: "last24", label: "Últimos 24 meses", monthsBack: 23 },
    { value: "todo", label: "Toda la historia", monthsBack: null },
];

const DEFAULT_RANGE = "last12";

// Helpers de rango — encapsulan la conversión range → (from, to). Se exportan
// para que los specs puedan testear sin instanciar el store. El rango se ancla
// en HOY: `to` es el día actual y `from` cuenta `monthsBack` meses hacia atrás
// desde el mes en curso (o el piso histórico cuando es "todo"). No depende de
// `monthIso` — el detalle del cliente tiene su propio rango, independiente del
// mes desde el que se abrió el modal padre.
export function computeRangeBoundaries(rangeValue) {
    const option = RANGE_OPTIONS.find((o) => o.value === rangeValue) ?? RANGE_OPTIONS[0];
    const now = moment();
    const from = option.monthsBack == null
        ? HISTORY_FLOOR
        : now.clone().subtract(option.monthsBack, "months").startOf("month").format("YYYY-MM-DD");
    return {
        from,
        to: now.format("YYYY-MM-DD"),
    };
}

export function computeMonthBoundaries(monthIso) {
    const start = moment(monthIso, "YYYY-MM").startOf("month");
    const end = moment(monthIso, "YYYY-MM").endOf("month");
    return {
        from: start.format("YYYY-MM-DD"),
        to: end.format("YYYY-MM-DD"),
    };
}

// Rango inicial del drawer al abrirse desde un mes puntual (el mes clickeado en
// la tabla de Ventas / en el ranking del modal de análisis). Devuelve el rango
// MÁS CHICO de RANGE_OPTIONS cuyo [from, to] contiene ese mes — así el período
// que ve el usuario cubre el mes del que vino y la data es relevante. Las
// opciones están ordenadas de menor a mayor cobertura (last12 < last24 < todo),
// y "todo" es el piso de seguridad: en el peor caso (mes muy viejo, futuro o
// inválido) cae ahí.
export function pickRangeForMonth(monthIso) {
    if (!monthIso) return "todo";
    const monthStart = moment(monthIso, "YYYY-MM").startOf("month");
    if (!monthStart.isValid()) return "todo";
    for (const option of RANGE_OPTIONS) {
        const { from, to } = computeRangeBoundaries(option.value);
        if (monthStart.isSameOrAfter(moment(from)) && monthStart.isSameOrBefore(moment(to))) {
            return option.value;
        }
    }
    return "todo";
}

// Eje X de la comparación: meses 1..12 (el chart los mapea a Ene..Dic).
export const COMPARISON_MONTHS = Object.freeze([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);

// Años (desc) con actividad real (algún monto > 0) en la serie mensual cruda.
// Alimenta el "+ Añadir año" y el default de años seleccionados.
export function counterpartComparisonYears(series) {
    const totalByYear = new Map();
    for (const row of series || []) {
        if (!row || !row.month_iso) continue;
        const year = Number(String(row.month_iso).split("-")[0]);
        if (Number.isNaN(year)) continue;
        totalByYear.set(year, (totalByYear.get(year) || 0) + (row.amount || 0));
    }
    return [...totalByYear.entries()]
        .filter(([, total]) => total > 0)
        .map(([year]) => year)
        .sort((a, b) => b - a);
}

// Pivot de la serie mensual multi-año en valores por año alineados a 12 meses,
// para el SalesComparisonChart. "monto" usa `amount`; "share" usa el % entero
// amount/sales_total (null si no hay ventas ese mes → la línea corta). Devuelve
// sólo los `selectedYears` que tengan datos, de más reciente a más antiguo.
// `nowIso` (YYYY-MM) define el corte del año en curso: los meses POSTERIORES al
// mes actual no tienen datos todavía → quedan en null para que la línea corte
// en el mes vigente en vez de caer a cero hacia diciembre (inyectable en tests).
export function buildCounterpartComparisonSeries(
    series, selectedYears, metric = "monto", nowIso = moment().format("YYYY-MM"),
) {
    const [nowYearStr, nowMonthStr] = String(nowIso).split("-");
    const nowYear = Number(nowYearStr);
    const nowMonthIdx = Number(nowMonthStr) - 1;

    const byYear = new Map();
    for (const row of series || []) {
        if (!row || !row.month_iso) continue;
        const [y, m] = String(row.month_iso).split("-");
        const year = Number(y);
        const monthIdx = Number(m) - 1;
        if (Number.isNaN(year) || monthIdx < 0 || monthIdx > 11) continue;
        if (!byYear.has(year)) {
            byYear.set(year, { amounts: Array(12).fill(0), totals: Array(12).fill(0) });
        }
        const bucket = byYear.get(year);
        bucket.amounts[monthIdx] = row.amount || 0;
        bucket.totals[monthIdx] = row.sales_total || 0;
    }
    return (selectedYears || [])
        .filter((year) => byYear.has(year))
        .sort((a, b) => b - a)
        .map((year) => {
            const { amounts, totals } = byYear.get(year);
            // Mes futuro del año en curso: sin datos aún → null (corta la línea).
            const isFuture = (i) => year === nowYear && i > nowMonthIdx;
            const values = metric === "share"
                ? amounts.map((amt, i) => (!isFuture(i) && totals[i] > 0 ? Math.round((amt / totals[i]) * 100) : null))
                : amounts.map((amt, i) => (isFuture(i) ? null : amt));
            return { year, values };
        });
}

const createState = () => ({
    /** YYYY-MM del mes clickeado en el ranking — define el alcance de docs. */
    monthIso: null,
    /** "C" (Cliente) o "P" (Proveedor). */
    documentType: null,
    /**
     * level_2 de comparación = el denominador "% de Ventas" del chart de
     * Historial, y también el subject cuando el drill es de Ventas. Siempre
     * "Ventas" (incluso en el drill de Costos, donde la línea sigue siendo % de
     * ventas).
     */
    level2Name: "Ventas",
    /**
     * Subject del counterpart por level_1 cuando aplica. null para Ventas
     * (subject = level_2); "Costos" para el drill desde Costos, porque los
     * level_2 de costos son sub-tipos (Costos Productos/Variables/Fijos) y
     * filtrar por uno solo perdería el resto. El backend lo recibe aparte.
     */
    level1Name: null,
    /**
     * Tipo de contraparte del ranking (Proveedor/Honorario/Trabajador/…).
     * Acota docs + serie a ese tipo en el drill de Costos, para que coincidan
     * con la metadata de la card. null = sin filtro de tipo (Ventas lo deriva
     * del document_type en el backend).
     */
    counterpartType: null,
    /** obligation_name del counterpart — necesario para la API. */
    obligationName: "Otros",
    ciId: null,
    /** Counterpart cargado vía `get_counterpart(single=true)`. */
    counterpart: null,
    /**
     * Documentos accruados al rango activo (último año / 6 meses / 2 años),
     * con `amount` prorrateado al período (no el monto facturado real — el
     * original queda en `original_amount` por si la UI lo necesita).
     */
    documents: [],
    /**
     * Suma prorrateada de los documentos del rango. Se sirve aparte porque
     * algunos docs aparecen en varios meses con porciones distintas y
     * `documents.reduce(amount)` ya da el total correcto — esta key sólo
     * evita el redondeo per-row.
     */
    totalRange: 0,
    /** Serie mensual `[{ month_iso, amount, sales_total }]` del rango. */
    monthlyHistory: [],
    /** Rango actual del selector (afecta sólo chart + KPI año). */
    rangePeriod: DEFAULT_RANGE,
    isLoading: false,
    error: null,
    /** Filtro de búsqueda libre sobre número de documento (case-insensitive). */
    search: "",

    // --- Comparación multi-año (toggle del chart de Historial) ---
    /** Serie mensual cruda de TODA la historia para pivotar por año. Lazy. */
    comparisonSeries: [],
    /** Ya se hizo el fetch de la comparación (evita refetch en cada toggle). */
    comparisonLoaded: false,
    isComparisonLoading: false,
    comparisonError: null,
    /** Años seleccionados en la comparación (default: los 3 más recientes con datos). */
    comparisonYears: [],
    /** Métrica comparada: "monto" ($) o "share" (% de ventas). */
    comparisonMetric: "monto",
});

const getters = {
    /** Documentos filtrados por `search` (matching contra doc_number). */
    filteredDocuments: (state) => {
        const q = state.search.trim().toLowerCase();
        if (!q) return state.documents;
        return state.documents.filter((doc) => {
            const num = String(doc.doc_number ?? "").toLowerCase();
            return num.includes(q);
        });
    },
    /** Cantidad de documentos del rango (KPI "Documentos del [rango]"). */
    documentsCount: (state) => state.documents.length,
    /**
     * Fecha aproximada del primer documento del counterpart, derivada del
     * primer mes con monto > 0 en la serie. Es una aproximación — la
     * granularidad es mensual. Si necesitamos día exacto, agregar otro
     * endpoint o derivar del documento más antiguo retornado.
     */
    firstActiveMonth: (state) => {
        const found = state.monthlyHistory.find((row) => (row.amount || 0) > 0);
        return found ? found.month_iso : null;
    },
    /** Label legible del rango actual (para el chip del selector). */
    rangeLabel: (state) => {
        const opt = RANGE_OPTIONS.find((o) => o.value === state.rangePeriod);
        return opt ? opt.label : "";
    },
    /** Años con actividad disponibles para la comparación (desc). */
    comparisonAvailableYears: (state) => counterpartComparisonYears(state.comparisonSeries),
    /**
     * View model para SalesComparisonChart: series por año (según métrica),
     * meses 1..12, años seleccionados/disponibles y el formato de valor.
     */
    comparisonViewModel: (state, getters) => ({
        series: buildCounterpartComparisonSeries(
            state.comparisonSeries, state.comparisonYears, state.comparisonMetric,
        ),
        months: COMPARISON_MONTHS,
        years: state.comparisonYears,
        availableYears: getters.comparisonAvailableYears,
        period: "month",
        valueFormat: state.comparisonMetric === "share" ? "percent" : "money",
    }),
};

const mutations = {
    OPEN(state, { ciId, monthIso, documentType, obligationName, level2Name, level1Name, counterpartType, rangePeriod }) {
        state.ciId = ciId;
        state.monthIso = monthIso;
        state.documentType = documentType;
        state.level2Name = level2Name || "Ventas";
        state.level1Name = level1Name || null;
        state.counterpartType = counterpartType || null;
        state.obligationName = obligationName || "Otros";
        // El rango inicial cubre el mes desde el que se abrió el drawer
        // (pickRangeForMonth); fallback al default si el caller no lo computó.
        state.rangePeriod = rangePeriod || DEFAULT_RANGE;
        state.search = "";
        // Limpieza explícita — el caller espera estado fresco aún si reabre
        // sobre el mismo cliente. No usamos RESET acá porque queremos
        // conservar los identificadores que acabamos de setear.
        state.counterpart = null;
        state.documents = [];
        state.totalRange = 0;
        state.monthlyHistory = [];
        state.error = null;
        // Comparación: fresca por contraparte (lazy, se carga al abrir el toggle).
        state.comparisonSeries = [];
        state.comparisonLoaded = false;
        state.isComparisonLoading = false;
        state.comparisonError = null;
        state.comparisonYears = [];
        state.comparisonMetric = "monto";
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_COUNTERPART(state, counterpart) {
        state.counterpart = counterpart;
    },
    SET_DOCUMENTS(state, documents) {
        state.documents = documents;
    },
    SET_TOTAL_RANGE(state, value) {
        state.totalRange = value;
    },
    SET_MONTHLY_HISTORY(state, series) {
        state.monthlyHistory = series;
    },
    SET_RANGE_PERIOD(state, value) {
        state.rangePeriod = value;
    },
    SET_SEARCH(state, value) {
        state.search = value;
    },
    SET_COMPARISON_SERIES(state, series) {
        state.comparisonSeries = series;
        state.comparisonLoaded = true;
    },
    SET_COMPARISON_LOADING(state, value) {
        state.isComparisonLoading = value;
    },
    SET_COMPARISON_ERROR(state, error) {
        state.comparisonError = error;
    },
    SET_COMPARISON_YEARS(state, years) {
        state.comparisonYears = years;
    },
    SET_COMPARISON_METRIC(state, metric) {
        state.comparisonMetric = metric;
    },
    RESET(state) {
        // Reusa el factory para no desincronizar el reset al agregar un campo.
        Object.assign(state, createState());
    },
};

const actions = {
    /**
     * Abre el drawer y carga las tres fuentes en paralelo:
     *  - counterpart (info general)
     *  - documents del mes (tabla)
     *  - monthly history (chart + KPI rango)
     *
     * Las tres llamadas son independientes — un fallo no debe abortar las
     * otras, así que las envolvemos en `Promise.allSettled` y reportamos
     * error sólo si las tres fallaron (criterio: el drawer es inútil sin
     * counterpart Y sin documents; el chart degrada bien sin history).
     */
    async openClientDetail(
        { commit },
        {
            ciId, alias, documentType, monthIso, obligationName,
            level2Name = "Ventas", level1Name = null, counterpartType = null,
        },
    ) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        // El clients_ranking del salesAnalysis devuelve sólo `name` (alias) —
        // resolvemos a ci_id acá si no vino explícito. La resolución vive antes
        // del OPEN para que el drawer reciba el ciId final en una sola tanda
        // de mutations en vez de re-commitear OPEN dos veces.
        let resolvedCiId = ciId;
        if (!resolvedCiId && alias) {
            try {
                const { data: resolved } = await resolve_counterpart_by_alias(alias, documentType);
                resolvedCiId = resolved?.ci_id ?? null;
            } catch (error) {
                consoleHandlerError({ error, prefix: "[clientDetail] resolve", silent: true });
            }
        }
        if (!resolvedCiId) {
            commit("SET_ERROR", "No se pudo identificar el cliente.");
            commit("SET_LOADING", false);
            return;
        }

        // Rango inicial: el más chico que cubra el mes del que se abrió el
        // drawer (modal de análisis / tabla de Ventas). Se usa tanto para el
        // estado del selector como para el fetch de docs/serie de abajo.
        const initialRange = pickRangeForMonth(monthIso);

        commit("OPEN", {
            ciId: resolvedCiId,
            monthIso,
            documentType,
            obligationName,
            level2Name,
            level1Name,
            counterpartType,
            rangePeriod: initialRange,
        });

        // try/finally: si algo síncrono lanza después del OPEN,
        // `isLoading` no debe quedar pegado en true. El finally garantiza el
        // reset siempre.
        try {
            const rangeBounds = computeRangeBoundaries(initialRange);
            const obl = obligationName || "Otros";

            // get_counterpart sigue con semántica emitted_at + obligation Otros —
            // es un endpoint compartido por toda la app y cambiarlo afectaría a
            // otros consumidores. Lo usamos solo para metadata (alias, rut, tipo,
            // clasificación, cuentas, atributos) que no depende del mes.
            //
            // Pasamos from_day/to_day = null para que el backend caiga en su
            // default de "últimos 100 años" — caso contrario, counterparts cuyos
            // docs aparecen en el ranking por DEVENGO al mes pero fueron EMITIDOS
            // en otro período, devuelven page_data vacío y el header queda sin
            // metadata (RUT/tipo/clasificación en blanco). Los KPIs "Total del
            // [rango]" y "Documentos del [rango]" salen de
            // get_counterpart_month_docs (que filtra por rango devengado).
            //
            // Docs y monthly history comparten el mismo rango — el selector del
            // header del drawer mueve ambos cuando cambia (changeRangePeriod).
            const [counterpartRes, docsRes, historyRes] = await Promise.allSettled([
                get_counterpart(resolvedCiId, documentType, null, null, obl),
                get_counterpart_month_docs(
                    resolvedCiId, rangeBounds.from, rangeBounds.to, documentType, level2Name, level1Name, counterpartType,
                ),
                get_counterpart_monthly_history(
                    resolvedCiId, documentType, rangeBounds.from, rangeBounds.to, level2Name, level1Name, counterpartType,
                ),
            ]);

            if (counterpartRes.status === "fulfilled") {
                const page = counterpartRes.value?.data?.page_data ?? [];
                commit("SET_COUNTERPART", page[0] ?? null);
            } else {
                consoleHandlerError({ error: counterpartRes.reason, prefix: "[clientDetail] counterpart", silent: true });
            }

            if (docsRes.status === "fulfilled") {
                commit("SET_DOCUMENTS", docsRes.value?.data?.documents ?? []);
                commit("SET_TOTAL_RANGE", docsRes.value?.data?.total_range ?? 0);
            } else {
                consoleHandlerError({ error: docsRes.reason, prefix: "[clientDetail] documents", silent: true });
            }

            if (historyRes.status === "fulfilled") {
                commit("SET_MONTHLY_HISTORY", historyRes.value?.data?.series ?? []);
            } else {
                consoleHandlerError({ error: historyRes.reason, prefix: "[clientDetail] history", silent: true });
            }

            const allFailed = [counterpartRes, docsRes, historyRes].every((r) => r.status === "rejected");
            if (allFailed) {
                commit("SET_ERROR", extractErrorMessage(counterpartRes.reason, "Error al cargar el detalle del cliente."));
            }
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * El usuario cambió el rango — refetcheamos en paralelo la lista de docs
     * y la serie mensual. Counterpart (metadata identitaria del cliente) no
     * depende del rango, así que no se toca. Promise.allSettled para que un
     * fallo aislado de una rama no aborte la otra.
     */
    async changeRangePeriod({ commit, state }, value) {
        commit("SET_RANGE_PERIOD", value);
        if (!state.ciId || !state.monthIso) return;
        const bounds = computeRangeBoundaries(value);

        const [docsRes, historyRes] = await Promise.allSettled([
            get_counterpart_month_docs(
                state.ciId,
                bounds.from,
                bounds.to,
                state.documentType,
                state.level2Name,
                state.level1Name,
                state.counterpartType,
            ),
            get_counterpart_monthly_history(
                state.ciId,
                state.documentType,
                bounds.from,
                bounds.to,
                state.level2Name,
                state.level1Name,
                state.counterpartType,
            ),
        ]);

        if (docsRes.status === "fulfilled") {
            commit("SET_DOCUMENTS", docsRes.value?.data?.documents ?? []);
            commit("SET_TOTAL_RANGE", docsRes.value?.data?.total_range ?? 0);
        } else {
            consoleHandlerError({ error: docsRes.reason, prefix: "[clientDetail] docs-refetch", silent: true });
        }

        if (historyRes.status === "fulfilled") {
            commit("SET_MONTHLY_HISTORY", historyRes.value?.data?.series ?? []);
        } else {
            consoleHandlerError({ error: historyRes.reason, prefix: "[clientDetail] history-refetch", silent: true });
        }
    },

    /**
     * Carga lazy de la comparación multi-año: una sola request de TODA la
     * historia de la contraparte (mismos filtros del drawer), pivotada por año
     * client-side. Se dispara al abrir el toggle de comparación; short-circuit
     * si ya se cargó. Default: los 3 años más recientes con actividad.
     */
    async loadCounterpartComparison({ commit, state }) {
        if (state.comparisonLoaded || !state.ciId) return;
        commit("SET_COMPARISON_LOADING", true);
        commit("SET_COMPARISON_ERROR", null);
        try {
            const { data } = await get_counterpart_monthly_history(
                state.ciId,
                state.documentType,
                HISTORY_FLOOR,
                moment().format("YYYY-MM-DD"),
                state.level2Name,
                state.level1Name,
                state.counterpartType,
            );
            const series = data?.series ?? [];
            commit("SET_COMPARISON_SERIES", series);
            commit("SET_COMPARISON_YEARS", counterpartComparisonYears(series).slice(0, 3));
        } catch (error) {
            // No marcamos loaded: reabrir el toggle reintenta.
            consoleHandlerError({ error, prefix: "[clientDetail] comparison", silent: true });
            commit("SET_COMPARISON_ERROR", extractErrorMessage(error, "Error al cargar la comparación."));
        } finally {
            commit("SET_COMPARISON_LOADING", false);
        }
    },

    addComparisonYear({ commit, state }, year) {
        if (state.comparisonYears.includes(year)) return;
        commit("SET_COMPARISON_YEARS", [...state.comparisonYears, year]);
    },

    removeComparisonYear({ commit, state }, year) {
        // Mantener al menos un año seleccionado (si no, el chart queda vacío).
        if (state.comparisonYears.length <= 1) return;
        commit("SET_COMPARISON_YEARS", state.comparisonYears.filter((y) => y !== year));
    },

    setComparisonMetric({ commit }, metric) {
        commit("SET_COMPARISON_METRIC", metric);
    },

    /**
     * El detalle se cierra desde fuera (close del drawer o cierre del modal
     * padre). RESET no toca nada más en otros módulos — el caller decide si
     * propaga o no a salesAnalysis.
     */
    closeClientDetail({ commit }) {
        commit("RESET");
    },

};

export default {
    namespaced: true,
    state: createState,
    getters,
    mutations,
    actions,
};
